import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { 
  Building2, 
  User, 
  Phone, 
  Mail, 
  MapPin,
  FileText,
  Calendar,
  CheckCircle,
  ArrowLeft,
  Users,
  Briefcase,
  Home,
  FileCheck
} from "lucide-react";

const ClientForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    // 1. Jurisdiction Preference
    jurisdiction: "",
    preferredEmirate: "",
    otherEmirate: "",
    
    // 2. Proposed Company Names
    companyName1: "",
    companyName2: "",
    companyName3: "",
    
    // 3. Business Activity
    primaryActivity: "",
    secondaryActivity: "",
    otherActivity1: "",
    otherActivity2: "",
    
    // 4. Company Structure
    numberOfPartners: "",
    customPartnerCount: "",
    ownershipType: "",
    partner1Percentage: "",
    partner2Percentage: "",
    partner3Percentage: "",
    
    // 5. Visa Requirements
    numberOfVisas: "",
    customVisaCount: "",
    visaType: "",
    
    // 6. Shareholder/Manager Details (Partner 1)
    fullName: "",
    nationality: "",
    dateOfBirth: "",
    passportNumber: "",
    passportExpiryDate: "",
    uaeResidencyStatus: "",
    mobileNumber: "",
    emailAddress: "",
    
    // Additional Partners (if needed)
    partner2FullName: "",
    partner2Nationality: "",
    partner2DateOfBirth: "",
    partner2PassportNumber: "",
    partner2PassportExpiryDate: "",
    partner2UaeResidencyStatus: "",
    partner2MobileNumber: "",
    partner2EmailAddress: "",
    
    partner3FullName: "",
    partner3Nationality: "",
    partner3DateOfBirth: "",
    partner3PassportNumber: "",
    partner3PassportExpiryDate: "",
    partner3UaeResidencyStatus: "",
    partner3MobileNumber: "",
    partner3EmailAddress: "",
    
    // 7. Required Documents Checklist
    passportCopy: false,
    uaeVisaPage: false,
    emiratesId: false,
    passportPhoto: false,
    proofOfAddress: false,
    
    // 8. Office Requirement
    officeRequirement: "",
    
    // 10. Client Declaration
    clientDeclaration: false,
    clientName: "",
    declarationDate: new Date().toISOString().split('T')[0]
  });

  const jurisdictions = [
    "Free Zone",
    "Mainland (DED)",
    "Not sure - Consultant to advise"
  ];

  const emirates = [
    "Dubai",
    "Sharjah", 
    "Ajman",
    "RAK",
    "UAQ"
  ];

  const partnerCounts = ["1", "2", "3", "More"];
  const visaCounts = ["0", "1", "2", "3", "More"];

  const ownershipTypes = [
    "Individual",
    "Partnership", 
    "Corporate Shareholder"
  ];

  const visaTypes = [
    "Investor",
    "Partner",
    "Employee"
  ];

  const residencyStatuses = [
    "Resident",
    "Visit Visa",
    "Outside UAE"
  ];

  const officeTypes = [
    "Flexi desk",
    "Shared office",
    "Private office",
    "Not required"
  ];

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.jurisdiction) {
      toast({
        title: "Missing Information",
        description: "Please select jurisdiction preference.",
        variant: "destructive"
      });
      return;
    }

    if (!formData.companyName1) {
      toast({
        title: "Missing Information",
        description: "Please provide at least one company name option.",
        variant: "destructive"
      });
      return;
    }

    if (!formData.primaryActivity) {
      toast({
        title: "Missing Information",
        description: "Please specify primary business activity.",
        variant: "destructive"
      });
      return;
    }

    if (!formData.fullName || !formData.nationality || !formData.passportNumber) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required shareholder details.",
        variant: "destructive"
      });
      return;
    }

    if (!formData.clientDeclaration) {
      toast({
        title: "Declaration Required",
        description: "Please confirm the client declaration to proceed.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for backend submission
      const submissionData = {
        // Basic client information
        full_name: formData.fullName,
        email: formData.emailAddress,
        phone: formData.mobileNumber,
        company_name: formData.companyName1,
        application_type: 'company_setup',
        
        // Company setup specific fields
        jurisdiction_preference: formData.jurisdiction,
        proposed_company_names: [formData.companyName1, formData.companyName2, formData.companyName3].filter(name => name),
        business_activity: `${formData.primaryActivity}${formData.secondaryActivity ? '; ' + formData.secondaryActivity : ''}`,
        company_structure: `${formData.numberOfPartners} partners, ${formData.ownershipType}`,
        visa_requirements: `${formData.numberOfVisas} visas, ${formData.visaType}`,
        office_requirement: formData.officeRequirement,
        
        // Shareholders information
        shareholders_info: {
          primary: {
            full_name: formData.fullName,
            nationality: formData.nationality,
            date_of_birth: formData.dateOfBirth,
            passport_number: formData.passportNumber,
            passport_expiry: formData.passportExpiryDate,
            uae_residency: formData.uaeResidencyStatus,
            mobile: formData.mobileNumber,
            email: formData.emailAddress,
            ownership_percentage: formData.partner1Percentage
          },
          ...(formData.partner2FullName && {
            partner2: {
              full_name: formData.partner2FullName,
              nationality: formData.partner2Nationality,
              date_of_birth: formData.partner2DateOfBirth,
              passport_number: formData.partner2PassportNumber,
              passport_expiry: formData.partner2PassportExpiryDate,
              uae_residency: formData.partner2UaeResidencyStatus,
              mobile: formData.partner2MobileNumber,
              email: formData.partner2EmailAddress,
              ownership_percentage: formData.partner2Percentage
            }
          }),
          ...(formData.partner3FullName && {
            partner3: {
              full_name: formData.partner3FullName,
              nationality: formData.partner3Nationality,
              date_of_birth: formData.partner3DateOfBirth,
              passport_number: formData.partner3PassportNumber,
              passport_expiry: formData.partner3PassportExpiryDate,
              uae_residency: formData.partner3UaeResidencyStatus,
              mobile: formData.partner3MobileNumber,
              email: formData.partner3EmailAddress,
              ownership_percentage: formData.partner3Percentage
            }
          })
        }
      };

      // Submit to Supabase Edge Function
      const response = await fetch('https://axlxwpuaqgbfkoampsai.supabase.co/functions/v1/handle_client_applications_20251222_1300_v2', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
        },
        body: JSON.stringify(submissionData)
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Application Submitted Successfully!",
          description: "Your company setup application has been received. We will contact you within 24 hours. Application ID: " + result.client_id?.slice(0, 8),
        });
        
        // Reset form
        setFormData({
          jurisdiction: "",
          preferredEmirate: "",
          otherEmirate: "",
          companyName1: "",
          companyName2: "",
          companyName3: "",
          primaryActivity: "",
          secondaryActivity: "",
          otherActivity1: "",
          otherActivity2: "",
          numberOfPartners: "",
          customPartnerCount: "",
          ownershipType: "",
          partner1Percentage: "",
          partner2Percentage: "",
          partner3Percentage: "",
          numberOfVisas: "",
          customVisaCount: "",
          visaType: "",
          fullName: "",
          nationality: "",
          dateOfBirth: "",
          passportNumber: "",
          passportExpiryDate: "",
          uaeResidencyStatus: "",
          mobileNumber: "",
          emailAddress: "",
          partner2FullName: "",
          partner2Nationality: "",
          partner2DateOfBirth: "",
          partner2PassportNumber: "",
          partner2PassportExpiryDate: "",
          partner2UaeResidencyStatus: "",
          partner2MobileNumber: "",
          partner2EmailAddress: "",
          partner3FullName: "",
          partner3Nationality: "",
          partner3DateOfBirth: "",
          partner3PassportNumber: "",
          partner3PassportExpiryDate: "",
          partner3UaeResidencyStatus: "",
          partner3MobileNumber: "",
          partner3EmailAddress: "",
          passportCopy: false,
          uaeVisaPage: false,
          emiratesId: false,
          passportPhoto: false,
          proofOfAddress: false,
          officeRequirement: "",
          clientDeclaration: false
        });
      } else {
        throw new Error('Failed to submit application');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your application. Please try again or contact us directly at info@eliteedgemc.ae",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPartnerDetails = (partnerNumber: number) => {
    const prefix = partnerNumber === 1 ? "" : `partner${partnerNumber}`;
    const title = partnerNumber === 1 ? "Primary Shareholder/Manager" : `Partner ${partnerNumber}`;
    
    return (
      <Card key={partnerNumber}>
        <CardHeader>
          <CardTitle className="flex items-center">
            <User className="h-5 w-5 mr-2" />
            {title} Details
          </CardTitle>
          <CardDescription>
            {partnerNumber === 1 ? "Primary" : "Additional"} shareholder/manager information
          </CardDescription>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor={`${prefix}fullName`}>Full Name (as per passport) *</Label>
            <Input
              id={`${prefix}fullName`}
              value={formData[`${prefix}fullName` as keyof typeof formData] as string}
              onChange={(e) => handleInputChange(`${prefix}fullName`, e.target.value)}
              placeholder="Enter full name as per passport"
              required={partnerNumber === 1}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}nationality`}>Nationality *</Label>
            <Input
              id={`${prefix}nationality`}
              value={formData[`${prefix}nationality` as keyof typeof formData] as string}
              onChange={(e) => handleInputChange(`${prefix}nationality`, e.target.value)}
              placeholder="Enter nationality"
              required={partnerNumber === 1}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}dateOfBirth`}>Date of Birth</Label>
            <Input
              id={`${prefix}dateOfBirth`}
              type="date"
              value={formData[`${prefix}dateOfBirth` as keyof typeof formData] as string}
              onChange={(e) => handleInputChange(`${prefix}dateOfBirth`, e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}passportNumber`}>Passport Number *</Label>
            <Input
              id={`${prefix}passportNumber`}
              value={formData[`${prefix}passportNumber` as keyof typeof formData] as string}
              onChange={(e) => handleInputChange(`${prefix}passportNumber`, e.target.value)}
              placeholder="Enter passport number"
              required={partnerNumber === 1}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}passportExpiryDate`}>Passport Expiry Date</Label>
            <Input
              id={`${prefix}passportExpiryDate`}
              type="date"
              value={formData[`${prefix}passportExpiryDate` as keyof typeof formData] as string}
              onChange={(e) => handleInputChange(`${prefix}passportExpiryDate`, e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}uaeResidencyStatus`}>UAE Residency Status</Label>
            <Select 
              value={formData[`${prefix}uaeResidencyStatus` as keyof typeof formData] as string} 
              onValueChange={(value) => handleInputChange(`${prefix}uaeResidencyStatus`, value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select residency status" />
              </SelectTrigger>
              <SelectContent>
                {residencyStatuses.map((status) => (
                  <SelectItem key={status} value={status.toLowerCase().replace(/\s+/g, '-')}>{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}mobileNumber`}>Mobile Number</Label>
            <Input
              id={`${prefix}mobileNumber`}
              value={formData[`${prefix}mobileNumber` as keyof typeof formData] as string}
              onChange={(e) => handleInputChange(`${prefix}mobileNumber`, e.target.value)}
              placeholder="+971 XX XXX XXXX"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}emailAddress`}>Email Address</Label>
            <Input
              id={`${prefix}emailAddress`}
              type="email"
              value={formData[`${prefix}emailAddress` as keyof typeof formData] as string}
              onChange={(e) => handleInputChange(`${prefix}emailAddress`, e.target.value)}
              placeholder="email@example.com"
            />
          </div>
        </CardContent>
      </Card>
    );
  };

  const numberOfPartnersInt = parseInt(formData.numberOfPartners === "More" ? formData.customPartnerCount : formData.numberOfPartners) || 1;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="./images/logo.jpg" 
                alt="Company Logo" 
                className="h-12 w-auto object-contain"
              />
              <span className="text-3xl font-bold text-primary">Elite Edge Management Consultancy</span>
            </Link>
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-foreground hover:text-primary transition-colors">Home</Link>
              <Link to="/services" className="text-foreground hover:text-primary transition-colors">Services</Link>
              <Link to="/login" className="text-foreground hover:text-primary transition-colors">Admin</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="py-8 px-4 bg-gradient-to-br from-primary/5 via-primary/10 to-accent/5">
        <div className="container mx-auto text-center">
          <Badge className="mb-4" variant="secondary">Client Information & Company Setup</Badge>
          <h1 className="text-4xl font-bold mb-4">Company Setup Application</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Complete this mandatory form to initiate your company setup process with UAE (Mainland) or relevant UAE Free Zone Authority.
            <br />
            <span className="text-sm font-medium text-orange-600">Incomplete information may result in processing delays.</span>
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* 1. Jurisdiction Preference */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MapPin className="h-5 w-5 mr-2" />
                  1. Jurisdiction Preference
                </CardTitle>
                <CardDescription>
                  Select your preferred jurisdiction for company setup
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  {jurisdictions.map((jurisdiction) => (
                    <div key={jurisdiction} className="flex items-center space-x-2">
                      <Checkbox
                        id={jurisdiction}
                        checked={formData.jurisdiction === jurisdiction}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            handleInputChange("jurisdiction", jurisdiction);
                          }
                        }}
                      />
                      <Label htmlFor={jurisdiction} className="text-sm font-medium">
                        {jurisdiction}
                      </Label>
                    </div>
                  ))}
                </div>
                
                <div className="space-y-4">
                  <Label className="text-sm font-medium">Preferred Emirate (if applicable):</Label>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {emirates.map((emirate) => (
                      <div key={emirate} className="flex items-center space-x-2">
                        <Checkbox
                          id={emirate}
                          checked={formData.preferredEmirate === emirate}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              handleInputChange("preferredEmirate", emirate);
                            }
                          }}
                        />
                        <Label htmlFor={emirate} className="text-sm">
                          {emirate}
                        </Label>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="other-emirate"
                      checked={formData.preferredEmirate === "Other"}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          handleInputChange("preferredEmirate", "Other");
                        }
                      }}
                    />
                    <Label htmlFor="other-emirate" className="text-sm">Other:</Label>
                    <Input
                      value={formData.otherEmirate}
                      onChange={(e) => handleInputChange("otherEmirate", e.target.value)}
                      placeholder="Specify other emirate"
                      className="w-48"
                      disabled={formData.preferredEmirate !== "Other"}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 2. Proposed Company Names */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Building2 className="h-5 w-5 mr-2" />
                  2. Proposed Company Names
                </CardTitle>
                <CardDescription>
                  Provide three options in order of preference
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName1">1. First Choice *</Label>
                  <Input
                    id="companyName1"
                    value={formData.companyName1}
                    onChange={(e) => handleInputChange("companyName1", e.target.value)}
                    placeholder="Enter first choice company name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyName2">2. Second Choice</Label>
                  <Input
                    id="companyName2"
                    value={formData.companyName2}
                    onChange={(e) => handleInputChange("companyName2", e.target.value)}
                    placeholder="Enter second choice company name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyName3">3. Third Choice</Label>
                  <Input
                    id="companyName3"
                    value={formData.companyName3}
                    onChange={(e) => handleInputChange("companyName3", e.target.value)}
                    placeholder="Enter third choice company name"
                  />
                </div>
              </CardContent>
            </Card>

            {/* 3. Business Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Briefcase className="h-5 w-5 mr-2" />
                  3. Business Activity
                </CardTitle>
                <CardDescription>
                  Specify your business activities
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="primaryActivity">Primary Activity *</Label>
                  <Textarea
                    id="primaryActivity"
                    value={formData.primaryActivity}
                    onChange={(e) => handleInputChange("primaryActivity", e.target.value)}
                    placeholder="Describe your primary business activity"
                    rows={3}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondaryActivity">Secondary Activity (if any)</Label>
                  <Textarea
                    id="secondaryActivity"
                    value={formData.secondaryActivity}
                    onChange={(e) => handleInputChange("secondaryActivity", e.target.value)}
                    placeholder="Describe your secondary business activity"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="otherActivity1">Other Activity (if any)</Label>
                  <Textarea
                    id="otherActivity1"
                    value={formData.otherActivity1}
                    onChange={(e) => handleInputChange("otherActivity1", e.target.value)}
                    placeholder="Describe other business activity"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="otherActivity2">Other Activity (if any)</Label>
                  <Textarea
                    id="otherActivity2"
                    value={formData.otherActivity2}
                    onChange={(e) => handleInputChange("otherActivity2", e.target.value)}
                    placeholder="Describe additional business activity"
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            {/* 4. Company Structure */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="h-5 w-5 mr-2" />
                  4. Company Structure
                </CardTitle>
                <CardDescription>
                  Define your company ownership structure
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <Label className="text-sm font-medium">Number of Partners / Shareholders:</Label>
                  <div className="flex gap-4">
                    {partnerCounts.map((count) => (
                      <div key={count} className="flex items-center space-x-2">
                        <Checkbox
                          id={`partners-${count}`}
                          checked={formData.numberOfPartners === count}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              handleInputChange("numberOfPartners", count);
                            }
                          }}
                        />
                        <Label htmlFor={`partners-${count}`} className="text-sm">
                          {count}
                        </Label>
                      </div>
                    ))}
                  </div>
                  {formData.numberOfPartners === "More" && (
                    <Input
                      value={formData.customPartnerCount}
                      onChange={(e) => handleInputChange("customPartnerCount", e.target.value)}
                      placeholder="Specify number of partners"
                      className="w-48"
                      type="number"
                      min="4"
                    />
                  )}
                </div>

                <div className="space-y-4">
                  <Label className="text-sm font-medium">Ownership Type:</Label>
                  <div className="flex gap-6">
                    {ownershipTypes.map((type) => (
                      <div key={type} className="flex items-center space-x-2">
                        <Checkbox
                          id={`ownership-${type}`}
                          checked={formData.ownershipType === type}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              handleInputChange("ownershipType", type);
                            }
                          }}
                        />
                        <Label htmlFor={`ownership-${type}`} className="text-sm">
                          {type}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {numberOfPartnersInt > 1 && (
                  <div className="space-y-4">
                    <Label className="text-sm font-medium">Shareholding Split (if applicable):</Label>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="partner1Percentage">Partner 1: % </Label>
                        <Input
                          id="partner1Percentage"
                          type="number"
                          min="0"
                          max="100"
                          value={formData.partner1Percentage}
                          onChange={(e) => handleInputChange("partner1Percentage", e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      {numberOfPartnersInt >= 2 && (
                        <div className="space-y-2">
                          <Label htmlFor="partner2Percentage">Partner 2: %</Label>
                          <Input
                            id="partner2Percentage"
                            type="number"
                            min="0"
                            max="100"
                            value={formData.partner2Percentage}
                            onChange={(e) => handleInputChange("partner2Percentage", e.target.value)}
                            placeholder="0"
                          />
                        </div>
                      )}
                      {numberOfPartnersInt >= 3 && (
                        <div className="space-y-2">
                          <Label htmlFor="partner3Percentage">Partner 3: %</Label>
                          <Input
                            id="partner3Percentage"
                            type="number"
                            min="0"
                            max="100"
                            value={formData.partner3Percentage}
                            onChange={(e) => handleInputChange("partner3Percentage", e.target.value)}
                            placeholder="0"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 5. Visa Requirements */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  5. Visa Requirements
                </CardTitle>
                <CardDescription>
                  Specify your visa requirements
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <Label className="text-sm font-medium">Number of visas required:</Label>
                  <div className="flex gap-4">
                    {visaCounts.map((count) => (
                      <div key={count} className="flex items-center space-x-2">
                        <Checkbox
                          id={`visas-${count}`}
                          checked={formData.numberOfVisas === count}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              handleInputChange("numberOfVisas", count);
                            }
                          }}
                        />
                        <Label htmlFor={`visas-${count}`} className="text-sm">
                          {count}
                        </Label>
                      </div>
                    ))}
                  </div>
                  {formData.numberOfVisas === "More" && (
                    <Input
                      value={formData.customVisaCount}
                      onChange={(e) => handleInputChange("customVisaCount", e.target.value)}
                      placeholder="Specify number of visas"
                      className="w-48"
                      type="number"
                      min="4"
                    />
                  )}
                </div>

                <div className="space-y-4">
                  <Label className="text-sm font-medium">Visa Type:</Label>
                  <div className="flex gap-6">
                    {visaTypes.map((type) => (
                      <div key={type} className="flex items-center space-x-2">
                        <Checkbox
                          id={`visa-type-${type}`}
                          checked={formData.visaType === type}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              handleInputChange("visaType", type);
                            }
                          }}
                        />
                        <Label htmlFor={`visa-type-${type}`} className="text-sm">
                          {type}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 6. Shareholder/Manager Details */}
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2">6. Shareholder / Manager Details</h2>
                <p className="text-muted-foreground">Complete details for each partner/shareholder</p>
              </div>
              
              {/* Render partner details based on number of partners */}
              {Array.from({ length: numberOfPartnersInt }, (_, index) => renderPartnerDetails(index + 1))}
            </div>

            {/* 7. Required Documents */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileCheck className="h-5 w-5 mr-2" />
                  7. Required Documents (Clear Color Copies)
                </CardTitle>
                <CardDescription>
                  Check all documents you can provide
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="passportCopy"
                      checked={formData.passportCopy}
                      onCheckedChange={(checked) => handleInputChange("passportCopy", checked)}
                    />
                    <Label htmlFor="passportCopy" className="text-sm">
                      Passport copy (min. 6 months validity)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="uaeVisaPage"
                      checked={formData.uaeVisaPage}
                      onCheckedChange={(checked) => handleInputChange("uaeVisaPage", checked)}
                    />
                    <Label htmlFor="uaeVisaPage" className="text-sm">
                      UAE visa page / entry stamp (if applicable)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="emiratesId"
                      checked={formData.emiratesId}
                      onCheckedChange={(checked) => handleInputChange("emiratesId", checked)}
                    />
                    <Label htmlFor="emiratesId" className="text-sm">
                      Emirates ID (front & back, if available)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="passportPhoto"
                      checked={formData.passportPhoto}
                      onCheckedChange={(checked) => handleInputChange("passportPhoto", checked)}
                    />
                    <Label htmlFor="passportPhoto" className="text-sm">
                      Passport-size photo (white background)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="proofOfAddress"
                      checked={formData.proofOfAddress}
                      onCheckedChange={(checked) => handleInputChange("proofOfAddress", checked)}
                    />
                    <Label htmlFor="proofOfAddress" className="text-sm">
                      Proof of address (even if outside UAE) such as: bills, tenancy contract, etc.
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 8. Office Requirement */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Home className="h-5 w-5 mr-2" />
                  8. Office Requirement
                </CardTitle>
                <CardDescription>
                  Select your office space requirement
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {officeTypes.map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={`office-${type}`}
                        checked={formData.officeRequirement === type}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            handleInputChange("officeRequirement", type);
                          }
                        }}
                      />
                      <Label htmlFor={`office-${type}`} className="text-sm">
                        {type}
                      </Label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* 10. Client Declaration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  10. Client Declaration
                </CardTitle>
                <CardDescription>
                  Please read and confirm the declaration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    I confirm that the information and documents provided above are true and accurate. 
                    I acknowledge that additional information or documents may be requested by the authority 
                    in UAE in relation to my request to obtain business trade license.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="clientName">Client Name *</Label>
                    <Input
                      id="clientName"
                      value={formData.clientName}
                      onChange={(e) => handleInputChange("clientName", e.target.value)}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="declarationDate">Date</Label>
                    <Input
                      id="declarationDate"
                      type="date"
                      value={formData.declarationDate}
                      onChange={(e) => handleInputChange("declarationDate", e.target.value)}
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="clientDeclaration"
                      checked={formData.clientDeclaration}
                      onCheckedChange={(checked) => handleInputChange("clientDeclaration", checked)}
                      required
                    />
                    <Label htmlFor="clientDeclaration" className="text-sm font-medium">
                      I confirm and agree to the above declaration *
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex justify-center pt-8">
              <Button 
                type="submit" 
                size="lg" 
                className="px-12 bg-gradient-to-r from-primary to-primary-glow"
                disabled={!formData.clientDeclaration || isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Company Setup Application"}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ClientForm;